import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { ErrorBag } from 'src/models/ErrorModel';
import { FormControl, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect } from 'react';
import { FORM_ID } from 'src/models/FormModel';
import { getGatewayListAction } from 'src/redux/actions/gatewayAction';
import { ProcessingType } from 'src/models/GatewayModel';

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: '100%',
    marginBottom: '8px',
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  minWidth: {
    minWidth: '480px',
  },
}));

interface Props {
  disabled?: boolean,
  form: FORM_ID,
  id?: string,
  margin?: 'dense' | 'normal' | undefined,
  name: string,
  noMarginTop?: boolean
  noMinWidth?: boolean
  onChange: (id:number) => void,
  type?: ProcessingType,
  value?: number,
}

interface DataItem {
  id: number,
  label?: string,
}

const GatewaySelector = (props: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const {
    disabled,
    form,
    id,
    margin,
    name,
    noMarginTop,
    noMinWidth,
    onChange,
    type,
    value,
  } = props;

  const data = useSelector((state: Store) => state.gateways.filter(
    (gateway) => (!!type ? (gateway.processing_type === type) : gateway),
  ));

  const getLabel = () => {
    switch (type) {
      case ProcessingType.cc:
        return 'Gateway for credit card';
      case ProcessingType.wallet:
        return 'Gateway for Apple Pay / Google Pay';
      default:
        return 'Gateway for EFT/ bank transfer';
    }
  };

  const errors: ErrorBag =
    useSelector((state:Store) => state.errors[form] || {} as ErrorBag);

  useEffect(() => {
    dispatch(getGatewayListAction());
  }, [type]);

  const getItems = () => {
    const items:DataItem[] = [{ id: 0 }];
    if (!data || !data.length) {
      return items;
    }
    data.sort(
      (a, b) => a.gateway_name.localeCompare(b.gateway_name),
    ).forEach((item: any) => {
      items.push({
        id: item.id,
        label: item.gateway_name,
      } as DataItem);
    });
    return items;
  };

  const onValueChange = (item: DataItem) => {
    if (!item) {
      onChange(0);
    }
    onChange(item.id as number);
  };

  const getValue = (initialValue?: number) => {
    let result = {
      id: 0,
      label: undefined,
    } as DataItem;
    if (!data || !data.length) {
      return result;
    }
    data.forEach((item: any) => {
      if (item.id === initialValue) {
        result = {
          id: item.id,
          label: item.gateway_name,
        } as DataItem;
      }
    });
    return result;
  };

  const renderLabel = (option:DataItem) => {
    if (option.label) {
      return (
        <span id={`${id}-option-${option.id}`}>{ option.label }</span>
      );
    }
    return (<></>);
  };

  const getOptionLabel = (option: DataItem) => option.label || '';

  const getClasses = () => {
    const noMarginTopClass = noMarginTop ? '' : classes.marginTop;
    const noMinWidthClass = noMinWidth ? '' : classes.minWidth;
    return `${noMarginTopClass} ${noMinWidthClass} ${classes.formControl}`;
  };

  if (!type) return (<></>);
  return (
    <FormControl
      id={id}
      variant="outlined"
      className={classes.formControl}>
      <Autocomplete
        disabled={disabled}
        className={getClasses()}
        value={getValue(value)}
        onChange={(e, item) => onValueChange(item as DataItem)}
        options={getItems() || []}
        getOptionLabel={(option: DataItem) => getOptionLabel(option)}
        getOptionSelected={(option, item) => option.id === item.id}
        renderOption={(option: DataItem) => renderLabel(option)}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={getLabel()}
            placeholder={getLabel()}
            error={!!errors[name]}
            helperText={errors[name] && errors[name].join(',')}
            margin={margin || 'normal'} />
        )} />
    </FormControl>
  );
};

export default GatewaySelector;
